import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@frontend/shadcn/components/ui/accordion';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@frontend/shadcn/components/ui/tabs';
import React, { useState, useMemo } from 'react';
import { ShopifyIcon } from '@revfluence/fresh-icons/brands/esm';
import { BoltIcon, TrophyIcon } from '@revfluence/fresh-icons/solid/esm';
import { GetMultipleShopifyStats_storeStats } from '../../queries/types/GetMultipleShopifyStats';
import RefreshMultipleShopifyStoreStats from '../RefreshMemberConversionDrawer/RefreshMultipleShopifyStoreStats';
import TiersPerformanceTable from './TiersPerformanceTable';
import { GetTierPerformanceStatsForOffer_getTierPerformanceStatsForOffer } from '../../queries/types/GetTierPerformanceStatsForOffer';
import BoostsPerformanceTable from './BoostsPerformanceTable';
import { GetBoostsPerformanceStatsForOffer_boostPerformances } from '../../queries/types/GetBoostsPerformanceStatsForOffer';
import { GetOfferById_offer_boosts } from '../../queries/types/GetOfferById';

interface OfferDashboardTabsProps {
  storeWiseConversions: GetMultipleShopifyStats_storeStats[];
  tierPerformanceData: GetTierPerformanceStatsForOffer_getTierPerformanceStatsForOffer[];
  boostsPerformanceData: { data: GetBoostsPerformanceStatsForOffer_boostPerformances[], boosts: GetOfferById_offer_boosts[] };
}

const allTabItems = [
  { id: 'boosts-performance', label: <span className="flex items-center gap-2"><TrophyIcon className="h-4 w-4 text-primary" />Boosts Performance</span> },
  { id: 'tiers-performance', label: <span className="flex items-center gap-2"><BoltIcon className="h-4 w-4 text-primary" />Tiers Performance</span> },
  { id: 'store-wise-conversions', label: <span className="flex items-center gap-2"><ShopifyIcon className="h-4 w-4 text-primary" />Store wise conversions</span> },
];

export function OfferDashboardTabs({
  storeWiseConversions,
  tierPerformanceData,
  boostsPerformanceData,
}: OfferDashboardTabsProps): JSX.Element {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const availableTabs = useMemo(() => {
    const tabs = [];

    if (boostsPerformanceData && Array.isArray(boostsPerformanceData.data) && boostsPerformanceData.data.length > 0) {
      tabs.push(allTabItems[0]);
    }

    if (tierPerformanceData && Array.isArray(tierPerformanceData) && tierPerformanceData.length > 0) {
      tabs.push(allTabItems[1]);
    }

    if (storeWiseConversions && Array.isArray(storeWiseConversions) && storeWiseConversions.length > 0) {
      tabs.push(allTabItems[2]);
    }

    return tabs;
  }, [storeWiseConversions, tierPerformanceData, boostsPerformanceData]);

  const [activeTab, setActiveTab] = useState(availableTabs[0]?.id || '');

  React.useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.find((tab) => tab.id === activeTab)) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab]);

  if (availableTabs.length === 0) {
    return null;
  }

  return (
    <Card className="flex flex-col items-start gap-4 p-0">
      <CardContent className="p-0 w-full">
        <Accordion
          type="single"
          defaultValue=""
          collapsible
          className="w-full"
          onValueChange={(value) => setIsCollapsed(!value)}
        >
          <AccordionItem value="offer-details">
            <AccordionTrigger className="flex flex-row items-center justify-between px-4 pt-3 pb-1 rounded-none !shadow-none bg-transparent w-full">
              <div className="w-max inline-flex">
                {availableTabs.map((tab) => (
                  <div
                    key={tab.id}
                    className={`px-4 flex-1 sm:flex-auto justify-start cursor-pointer py-3 ${activeTab === tab.id && !isCollapsed ? 'border-b-2 border-primary font-medium' : ''
                      }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveTab(tab.id);
                    }}
                  >
                    {tab.label}
                  </div>
                ))}
              </div>
            </AccordionTrigger>
            <div className="border-b border-gray-200 w-full mt-[-11px]" />
            <AccordionContent className="p-0">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="hidden">
                  {availableTabs.map((tab) => (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.id}
                      className="hidden"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent className="p-4" value="boosts-performance">
                  <BoostsPerformanceTable data={boostsPerformanceData} />
                </TabsContent>
                <TabsContent className="p-4" value="tiers-performance">
                  <TiersPerformanceTable data={tierPerformanceData} />
                </TabsContent>
                <TabsContent className="p-4" value="store-wise-conversions">
                  <RefreshMultipleShopifyStoreStats data={storeWiseConversions} />
                </TabsContent>
              </Tabs>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
}

export default OfferDashboardTabs;
